import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AsyncPaginate, wrapMenuList } from "react-select-async-paginate";
import { components } from "react-select";
import { toast } from "react-toastify";
import NO_IMAGE from "../../assets/images/no-image-icon.png";
import countryApi from "api/countryApi";
import countries from "helpers/countries";

interface Option {
  label: string | any;
  value: string;
}
interface DropdownOptionProps {
  name?: string;
  listCheckedUsers?: string | null;
  debounceTimeout?: number;
  onChangeSelect?: (params: Option) => void;
  isMulti?: boolean;
  placeholder?: string;
  isShowIconCheck?: boolean;
  hideSelectedOptions?: boolean;
  isAllOption?: boolean;
  isDisabled?: boolean;
  isNotBelongToCountries?: boolean;
  isClearable?: boolean;
  keySetValue?: string;
}
const LIMIT = 30;

const DropdownAsyncCountries = ({
  name = "",
  listCheckedUsers,
  debounceTimeout = 700,
  onChangeSelect,
  isMulti = false,
  placeholder,
  isShowIconCheck = false,
  hideSelectedOptions = true,
  isAllOption = false,
  isDisabled = false,
  isNotBelongToCountries = false,
  isClearable = true,
  keySetValue = "",
}: DropdownOptionProps) => {
  const { t, i18n } = useTranslation();
  const [isAllCheckedUsers, setIsAllCheckedUsers] = useState(false);
  const [value, setValue] = useState<any>(listCheckedUsers);

  const OPTION_ALL_DEFAULT = { label: t("All Filter"), value: "all" };
  const OPTION_NOT_BELONG_TO_COUNTRIES_DEFAULT = {
    label: t("Not belong to countries"),
    value: "not",
  };

  const [options, setOptions] = useState([]);

  const setOnChangeOption = (item: any) => {
    setValue(item);
    onChangeSelect && onChangeSelect(item);
  };

  const loadListCountries = async ({
    page,
    limit,
    search,
  }: {
    page: number;
    limit: number;
    search?: string;
  }) => {
    try {
      const { data, code } = await countryApi.countries({
        page: 1,
        limit: 20000,
      });

      if (!code) {
        return { success: false };
      }

      const countriesFilter = data.map((el: any) => {
        var dataNew = { ...el };
        countries.filter((f: any) => {
          if (f.code === el.code) {
            dataNew = {
              ...el,
              flags: f?.flags,
              name_korea: f?.name_korea,
              name: f?.name,
            };
          }
          return f.code === el.code;
        });
        return dataNew;
      });

      const dataUsersOptions = countriesFilter?.map((a: any) => {
        return {
          value: String(keySetValue ? a?.[keySetValue] : a?.id),
          label: (
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  height: "22px",
                  width: "22px",
                  overflow: "hidden",
                  borderRadius: "15px",
                  objectFit: "cover",
                  backgroundColor: "#eaeaea",
                  zIndex: 99,
                }}
              >
                <img
                  src={a?.flags || NO_IMAGE}
                  alt={a?.name}
                  height={22}
                  width={22}
                  style={{
                    minWidth: "22px",
                    overflow: "hidden",
                    borderRadius: "50%",
                    objectFit: "cover",
                    backgroundColor: "#eaeaea",
                  }}
                />
              </div>
              {a?.name}
            </div>
          ),
          data: a,
        };
      });

      if (!(listCheckedUsers as any)?.[0]?.value && !!listCheckedUsers?.[0]) {
        const arr = (listCheckedUsers as any)?.map((item: any) => {
          const temp = dataUsersOptions?.find((a: any) => a?.value === item);
          return temp;
        });
        setValue(arr);
      }
      setOptions(dataUsersOptions);
      return {
        code,
        dataUsersOptions: dataUsersOptions || [],
      };
    } catch (err) {
      return {
        code: 404,
        dataUsersOptions: [],
        lastPage: 1,
      };
    }
  };

  async function loadOptions(
    search: string,
    loadedOptions: any[],
    { page }: { page: number }
  ) {
    const arr = options?.filter((item: any) => {
      return item?.label?.props?.children?.[1]
        .toLowerCase()
        .includes(search?.toLowerCase());
    });
    return {
      options: arr,
    };
  }

  const MenuList = (props: any) => {
    let childComponent = props?.children;
    if (props?.children?.length > 0) {
      const allOption = props?.children?.filter(
        (item: any) => item?.props?.value === OPTION_ALL_DEFAULT?.value
      );
      let fixedIndex = -1;
      if (allOption) {
        fixedIndex = props?.children.indexOf(allOption?.[0]);
      }
      const fixedElement = props?.children?.[fixedIndex];

      const childComponentFormat = (
        (fixedIndex !== -1 ? props?.children.slice(1) : props?.children) || []
      )?.sort((a: any, b: any) => {
        let pointA = a.props.isSelected === true ? 1 : 0;
        let pointB = b.props.isSelected === true ? 1 : 0;
        return pointB - pointA;
      });

      if (fixedElement) {
        childComponent = [fixedElement, ...childComponentFormat];
      } else {
        childComponent = childComponentFormat;
      }
    }

    return (
      <components.MenuList {...props}>{childComponent}</components.MenuList>
    );
  };

  const OptionFormat = (props: any) => {
    if (props?.isSelected && props?.value === OPTION_ALL_DEFAULT?.value) {
      setIsAllCheckedUsers(true);
    } else if (
      !props?.isSelected &&
      props?.value === OPTION_ALL_DEFAULT?.value
    ) {
      setIsAllCheckedUsers(false);
    }

    return (
      <components.Option {...props} style={{ zIndex: 999 }}>
        <div className="d-flex align-items-center gap-2">
          {(props?.isSelected || isAllCheckedUsers) && isShowIconCheck ? (
            <i className="ri-check-line align-middle fs-14" />
          ) : (
            ""
          )}
          {props.data?.label}
        </div>
      </components.Option>
    );
  };

  const MenuListAsync = wrapMenuList(MenuList);

  const optionsMemo = useMemo(() => {
    return [
      ...(isAllOption ? [OPTION_ALL_DEFAULT] : []),
      ...(isNotBelongToCountries
        ? [OPTION_NOT_BELONG_TO_COUNTRIES_DEFAULT]
        : []),
      ...options,
    ];
  }, [
    JSON.stringify(options),
    isAllOption,
    isNotBelongToCountries,
    i18n?.language,
  ]);

  useEffect(() => {
    // if (!(listCheckedUsers as any)?.[0]?.value && !!listCheckedUsers?.[0]) {
    //   loadListCountries({ page: 1, limit: LIMIT });
    // }
    loadListCountries({ page: 1, limit: LIMIT });
  }, [listCheckedUsers]);

  useEffect(() => {
    if (
      ((listCheckedUsers as any)?.value === OPTION_ALL_DEFAULT.value ||
        !(listCheckedUsers as any)?.value) &&
      isAllOption
    ) {
      setOnChangeOption(OPTION_ALL_DEFAULT);
    }
  }, [i18n.language, isAllOption, JSON.stringify(listCheckedUsers)]);

  return (
    <AsyncPaginate
      key={i18n.language}
      name={name}
      isMulti={isMulti}
      isClearable={isClearable}
      // defaultOptions
      options={optionsMemo}
      debounceTimeout={debounceTimeout}
      value={value}
      loadOptions={loadOptions}
      placeholder={placeholder}
      className="dropdown-status-rounded"
      hideSelectedOptions={false}
      onChange={(e: any) => setOnChangeOption(e)}
      additional={{
        page: 1,
      }}
      components={{
        Option: OptionFormat,
        ...(isMulti ? { MenuList: MenuListAsync } : null),
        // MenuList: MenuListAsync,
      }}
      closeMenuOnSelect={hideSelectedOptions}
      menuPortalTarget={document.body}
      styles={{
        option: (styles: any) => {
          let styleNew = isMulti
            ? {
                ...styles,
                backgroundColor: "white",
                zIndex: 99,
                color: "black",
              }
            : styles;

          return styleNew;
        },
        control: (styles: any, { isFocused }: any) => {
          return {
            ...styles,
            borderColor: "#e9ebec",
            borderRadius: "0.6rem",
            color: "white",
            "&:hover": { borderColor: "#009ef7" },
          };
        },
        multiValue: (styles: any) => {
          return {
            ...styles,
            backgroundColor: "#009ef7",
          };
        },
        multiValueLabel: (styles: any) => ({
          ...styles,
          color: "white !important",
        }),
        menuPortal: (base: any) => ({
          ...base,
          zIndex: 1051,
        }),
      }}
      isDisabled={isDisabled}
    />
  );
};

export default DropdownAsyncCountries;
